import { Head } from '@inertiajs/react';
import type { CSSProperties, ReactNode } from 'react';
import type { LandingsEmailHeader } from '@/types';

// `app/(public)/26-{ar,co,mx}/email-header/page.tsx` — the 3:1 banner, screenshotted by
// hand. One page for the three stops (PORTING.md G5); Buenos Aires inverts the palette
// and splits the last line into a bold city and a light date, the other two keep the
// `theme-ph26` card and put the city on the "LATAM Tour" line.
const HATCH_STYLE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.14) 0px, rgba(255, 255, 255, 0.14) 1px, transparent 1px, transparent 12px), repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0px, rgba(0, 0, 0, 0.08) 1px, transparent 1px, transparent 12px)',
  backgroundPosition: '0 0, 1px 1px',
  opacity: 0.8,
};

type EmailHeaderEdition = {
  cardClass: string;
  logoClass: string;
  tourLineClass: string;
  tourLine: string;
  dateLineClass: string;
  dateLine: ReactNode;
};

const PH26_LOGO_CLASS =
  'whitespace-nowrap text-center font-logo text-[clamp(30px,6.2vw,50px)] text-foreground lowercase leading-[0.9] tracking-[-0.03em]';
const PH26_TOUR_LINE_CLASS =
  'mt-2 font-title text-[clamp(14px,2.6vw,20px)] text-foreground uppercase tracking-[0.22em]';
const PH26_DATE_LINE_CLASS =
  'mt-1 text-center font-sans text-[clamp(11px,1.9vw,14px)] text-foreground uppercase tracking-[0.15em]';
const PH26_CARD_CLASS =
  'theme-ph26 relative w-full max-w-[600px] overflow-hidden border border-foreground/30 bg-background';

const EDITIONS: Record<string, EmailHeaderEdition> = {
  '26-ar': {
    cardClass:
      'relative w-full max-w-[600px] overflow-hidden border border-background/30 bg-primary',
    logoClass:
      'whitespace-nowrap text-center font-logo text-[clamp(30px,6.2vw,50px)] text-background lowercase leading-[0.9] tracking-[-0.03em]',
    tourLineClass:
      'mt-2 font-title text-[clamp(10px,1.8vw,14px)] text-background uppercase tracking-[0.22em]',
    tourLine: 'LATAM Tour',
    dateLineClass:
      'mt-1 text-center font-title text-[clamp(11px,1.9vw,14px)] text-background uppercase tracking-[0.15em]',
    dateLine: (
      <>
        <span className="font-bold">buenos aires</span>{' '}
        <span className="font-normal">- 8 a 10 de mayo</span>
      </>
    ),
  },
  '26-co': {
    cardClass: PH26_CARD_CLASS,
    logoClass: PH26_LOGO_CLASS,
    tourLineClass: PH26_TOUR_LINE_CLASS,
    tourLine: 'LATAM Tour - Bogotá',
    dateLineClass: PH26_DATE_LINE_CLASS,
    dateLine: '21 a 23 de agosto',
  },
  '26-ve': {
    cardClass: PH26_CARD_CLASS,
    logoClass: PH26_LOGO_CLASS,
    tourLineClass: PH26_TOUR_LINE_CLASS,
    tourLine: 'LATAM Tour - Caracas',
    dateLineClass: PH26_DATE_LINE_CLASS,
    dateLine: '23 a 25 de octubre',
  },
  '26-mx': {
    cardClass: PH26_CARD_CLASS,
    logoClass: PH26_LOGO_CLASS,
    tourLineClass: PH26_TOUR_LINE_CLASS,
    tourLine: 'LATAM Tour - Ciudad de México',
    dateLineClass: PH26_DATE_LINE_CLASS,
    dateLine: '19 a 21 de junio',
  },
  '26-cl': {
    cardClass: PH26_CARD_CLASS,
    logoClass: PH26_LOGO_CLASS,
    tourLineClass: PH26_TOUR_LINE_CLASS,
    tourLine: 'LATAM Tour - Santiago',
    dateLineClass: PH26_DATE_LINE_CLASS,
    dateLine: '20 a 22 de noviembre',
  },
};

export default function EmailHeader({
  title,
  description,
  editionSlug,
}: LandingsEmailHeader) {
  const edition = EDITIONS[editionSlug];

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
      </Head>
      <div className="flex min-h-screen items-center justify-center bg-background p-6 md:p-10">
        <div className={edition.cardClass} style={{ aspectRatio: '3 / 1' }}>
          <div className="absolute inset-0" style={HATCH_STYLE} />

          <div className="relative z-10 flex h-full flex-col items-center justify-center px-3 md:px-4">
            <h1 className={edition.logoClass}>
              <span className="font-light">platanus hack</span>{' '}
              <span className="font-medium">[26]</span>
            </h1>
            <p className={edition.tourLineClass}>{edition.tourLine}</p>
            <p className={edition.dateLineClass}>{edition.dateLine}</p>
          </div>
        </div>
      </div>
    </>
  );
}
